import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Check, ArrowRight, ExternalLink, BookOpen, Users, FileText, PartyPopper, Sparkles, Video, Calendar, Clock, Loader2, MessageSquare, MoreVertical, Play } from 'lucide-react';
import PhaseIntroCard from '@/features/dashboard/PhaseIntroCard';
import CallAttendanceModal from '@/features/dashboard/CallAttendanceModal';
import VimeoEmbed from '@/components/VimeoEmbed';
import { useAuth } from '@/features/auth/AuthContext';
import { supabase } from '@/lib/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useCohort } from '@/features/dashboard/useCohort';
import type { FastTrackMode } from '@/features/dashboard/useFastTrackConfig';
import { format, addDays } from 'date-fns';
import { refUrl } from '@/lib/partners';

const CALL_DURATION_MINUTES = 90;

function parseCallHourMinute(timeStr: string): { hour: number; minute: number } | null {
  const atPart = timeStr.split(' at ')[1] ?? timeStr;
  const match = atPart.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!match) return null;
  let hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  const ampm = match[3].toUpperCase();
  if (ampm === 'PM' && hour !== 12) hour += 12;
  if (ampm === 'AM' && hour === 12) hour = 0;
  return { hour, minute };
}

function isCallEndedToday(timeStr: string): boolean {
  const parsed = parseCallHourMinute(timeStr);
  if (!parsed) return false;
  const etString = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });
  const etNow = new Date(etString);
  const callEndMinutes = parsed.hour * 60 + parsed.minute + CALL_DURATION_MINUTES;
  const nowMinutes = etNow.getHours() * 60 + etNow.getMinutes();
  return nowMinutes >= callEndMinutes;
}

function getNextCallDate(timeStr: string): Date {
  const DAY_MAP: Record<string, number> = {
    sunday: 0, sundays: 0,
    monday: 1, mondays: 1,
    tuesday: 2, tuesdays: 2,
    wednesday: 3, wednesdays: 3,
    thursday: 4, thursdays: 4,
    friday: 5, fridays: 5,
    saturday: 6, saturdays: 6,
  };
  const firstWord = timeStr.toLowerCase().split(/[\s,]+/)[0] ?? '';
  const targetDay = DAY_MAP[firstWord] ?? 2;
  const now = new Date();
  const daysUntil = (targetDay - now.getDay() + 7) % 7;

  if (daysUntil === 0 && isCallEndedToday(timeStr)) {
    return addDays(now, 7);
  }
  return addDays(now, daysUntil);
}

const FB_URL = 'https://www.facebook.com/groups/842778640733978';
const COURSE_URL = '/content';
const DECLARATION_URL = '/financial-freedom-declaration';

interface HomeworkStepProps {
  icon: React.ElementType;
  title: string;
  description: string;
  completed: boolean;
  onToggle: () => void;
  buttonLabel?: string;
  buttonIcon?: React.ElementType;
  onButtonClick?: () => void;
}

const HomeworkStep = ({ icon: Icon, title, description, completed, onToggle, buttonLabel, buttonIcon: BtnIcon, onButtonClick }: HomeworkStepProps) => (
  <div className={`rounded-xl border transition-all ${completed ? 'bg-emerald-50/50 border-emerald-200/60' : 'bg-card border-border hover:border-gold/30 hover:shadow-sm'}`}>
    <div className="p-4">
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${completed ? 'bg-emerald-100' : 'bg-gold/15'}`}>
          <Icon className={`h-4 w-4 ${completed ? 'text-emerald-600' : 'text-gold'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${completed ? 'text-emerald-700' : 'text-foreground'}`}>{title}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
          {buttonLabel && onButtonClick && !completed && (
            <Button
              onClick={onButtonClick}
              size="sm"
              className="mt-3 gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90 transition-opacity text-xs h-8"
            >
              {BtnIcon && <BtnIcon className="h-3.5 w-3.5 mr-1.5" />}
              {buttonLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
    <button
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-b-xl border-t transition-all font-medium ${
        completed
          ? 'bg-emerald-50 border-emerald-200/60 text-emerald-700 hover:bg-emerald-100/70'
          : 'bg-gold/10 border-gold/20 text-gold hover:bg-gold/15'
      }`}
      aria-label={completed ? 'Mark incomplete' : 'Mark complete'}
    >
      <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
        completed ? 'bg-emerald-500 border-emerald-500' : 'border-gold bg-transparent'
      }`}>
        {completed && <Check className="h-3 w-3 text-white" />}
      </div>
      <span className="text-sm">{completed ? 'Completed' : 'Mark Complete'}</span>
    </button>
  </div>
);

interface WeekTask {
  icon: React.ElementType;
  title: string;
  description: string;
  buttonLabel?: string;
  buttonIcon?: React.ElementType;
  url?: string;
}

interface WeekHomeworkProps {
  tasks: WeekTask[];
  homeworkItems: boolean[];
  onToggleItem: (index: number) => void;
  onStartDeclaration?: () => void;
  onGoToCourse?: () => void;
}

// Phase 2 is member-only, so members land on the page carrying their benefit.
const BETTERLIFE_REF_URL = refUrl({ placement: 'phase2_homework', isMember: true });

const WEEK_1_TASKS: WeekTask[] = [
  { icon: Video, title: 'Attend the Week 1 Fast Track Call', description: 'Join the live call to kick off your Fast Track journey.' },
  { icon: FileText, title: 'Complete "The Financial Freedom Declaration"', description: 'Your 250-400 word vision document. (This is the longer companion to the Phase 1 First Deal Declaration.)', buttonLabel: 'Start Declaration', buttonIcon: ExternalLink, url: DECLARATION_URL },
  { icon: Users, title: 'Make an Introduction Post', description: 'Introduce yourself in the First Deal Community Facebook group.', buttonLabel: 'Go to Community', buttonIcon: ExternalLink, url: FB_URL },
  { icon: BookOpen, title: 'Watch Modules 1 & 2', description: 'Complete the first two modules of the First Deal Real Estate Investing Course.', buttonLabel: 'Go to Course', buttonIcon: BookOpen, url: COURSE_URL },
];

const WEEK_2_TASKS: WeekTask[] = [
  { icon: Video, title: 'Attend the Week 2 Fast Track Call', description: 'Join the live call for this week\'s lesson and discussion.' },
  { icon: BookOpen, title: 'Watch Foundations Course Modules 3, 4 & 5', description: 'Continue the First Deal Real Estate Investing Course.', buttonLabel: 'Go to Course', buttonIcon: BookOpen, url: COURSE_URL },
];

const WEEK_3_TASKS: WeekTask[] = [
  { icon: Video, title: 'Attend the Week 3 Fast Track Call', description: 'Join the live call to explore off-market strategies.' },
  { icon: FileText, title: 'Decide on One Off-Market Strategy', description: 'Explore the off-market strategies discussed in the Fast Track Week 3 session and pick one that you can focus on and commit to consistently.' },
  { icon: BookOpen, title: 'Watch Modules 6 & 7', description: 'Continue the First Deal Real Estate Investing Course.', buttonLabel: 'Go to Course', buttonIcon: BookOpen, url: COURSE_URL },
];

const WEEK_4_TASKS: WeekTask[] = [
  { icon: Video, title: 'Attend the Week 4 Fast Track Call', description: 'Join the live call to discuss financing and pre-approval.' },
  { icon: FileText, title: 'Apply for Pre-Approval with BetterLife REF', description: 'Get pre-approved through BetterLife REF. First Deal members get $2,000 off their first loan with REF!', buttonLabel: 'Apply Now', buttonIcon: ExternalLink, url: BETTERLIFE_REF_URL },
  { icon: Users, title: 'Network Locally', description: 'Find and attend the next local real estate club or meetup in your area.' },
  { icon: BookOpen, title: 'Watch Modules 8 & 9', description: 'Complete Modules 8 & 9 and any additional homework found at the end of each module.', buttonLabel: 'Go to Course', buttonIcon: BookOpen, url: COURSE_URL },
];

const WEEK_5_TASKS: WeekTask[] = [
  { icon: Video, title: 'Attend the Week 5 Fast Track Call', description: 'Join the final Fast Track call to discuss disciplines and habits.' },
  { icon: FileText, title: 'Define 5-12 HACKs for Your Disciplines Tracker', description: 'Choose 5-12 HACKs that YOU will add to YOUR Disciplines Tracker.' },
  { icon: FileText, title: 'Start Tracking Your HACKs', description: 'Begin tracking those HACKs tomorrow and bring your habit tracker to the Bottleneck Breakthrough call.' },
  { icon: BookOpen, title: 'Finish Foundations Course -- Modules 10, 11 & 12', description: 'Complete the remaining modules of the First Deal Real Estate Investing Course.', buttonLabel: 'Go to Course', buttonIcon: BookOpen, url: COURSE_URL },
];

const WEEK_TASKS: WeekTask[][] = [WEEK_1_TASKS, WEEK_2_TASKS, WEEK_3_TASKS, WEEK_4_TASKS, WEEK_5_TASKS];

export const WEEK_TASK_COUNTS = WEEK_TASKS.map(tasks => tasks.length);

/**
 * Item 0 of every week is the session itself. In live mode that means attending
 * the Zoom call; in recorded mode it means watching the recording embedded in
 * the week card. Swapping it in place (rather than adding an item) keeps the
 * task counts identical, so `fast_track_weeks.homeworkItems` needs no migration
 * and a member mid-program keeps their progress when the mode flips.
 */
export function getWeekTasks(weekIdx: number, mode: FastTrackMode): WeekTask[] {
  const tasks = WEEK_TASKS[weekIdx];
  if (!tasks) return [];
  if (mode === 'live') return tasks;
  return [
    {
      icon: Play,
      title: `Watch the Week ${weekIdx + 1} Fast Track Recording`,
      description: 'Watch the full session recording above, then work through the rest of this week.',
    },
    ...tasks.slice(1),
  ];
}

const WeekHomework = ({ tasks, homeworkItems, onToggleItem, onStartDeclaration, onGoToCourse }: WeekHomeworkProps) => {
  const resolveButtonClick = (url?: string) => {
    if (!url) return undefined;
    if (url === DECLARATION_URL) return onStartDeclaration;
    if (url === COURSE_URL) return onGoToCourse;
    return () => window.open(url, '_blank', 'noopener');
  };

  return (
    <div className="space-y-3">
      {tasks.map((task, idx) => (
        <HomeworkStep
          key={idx}
          icon={task.icon}
          title={task.title}
          description={task.description}
          completed={homeworkItems[idx] ?? false}
          onToggle={() => onToggleItem(idx)}
          buttonLabel={task.buttonLabel}
          buttonIcon={task.buttonIcon}
          onButtonClick={resolveButtonClick(task.url)}
        />
      ))}
    </div>
  );
};

// Canonical week topics -- used in display regardless of what's stored in DB
const WEEK_TOPICS = ['Discovery', 'Decisions', 'Deal Flow', 'Dollars & Debt', 'Disciplines'];
const WEEK_ORDINALS = ['Week One', 'Week Two', 'Week Three', 'Week Four', 'Week Five'];
const WEEK_DESCRIPTIONS = [
  'Discover the program essentials.',
  'Set criteria and evaluate options.',
  'Source leads and analyze deals.',
  'Finance structures and budgets.',
  'Operational routines and habits.',
];

const FEEDBACK_QUESTIONS = [
  "Fast Track content was clear and easy to follow.",
  "Fast Track added real value to where I am in my real estate journey.",
  "After completing Fast Track, I feel more confident about my next steps.",
  "I can clearly identify at least one action I should take as a result of Fast Track.",
  "The Fast Track structure gave me what I needed.",
  "At this stage of my journey, the Fast Track instructor was engaging and helpful.",
];

// Sentinel value for expandedWeek to indicate the feedback step is expanded
const FEEDBACK_STEP = 5;

const Phase2 = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmWeek, setConfirmWeek] = useState<number | null>(null);
  const [expandedWeek, setExpandedWeek] = useState<number | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const { cohortCallLink, cohortCallTime, currentWeek, mode, recordings, loading } = useCohort();
  const isRecorded = mode === 'recorded';
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [pendingAttendanceWeek, setPendingAttendanceWeek] = useState<number | null>(null);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [checkingFeedback, setCheckingFeedback] = useState(true);
  const [feedbackForm, setFeedbackForm] = useState({ q1: 0, q2: 0, q3: 0, q4: 0, q5: 0, q6: 0, improvement_text: '' });

  const allCompleteCheck = user ? user.fastTrackWeeks.filter(w => w.completed).length === 5 : false;

  useEffect(() => {
    if (!allCompleteCheck || !user?.id) { setCheckingFeedback(false); return; }
    supabase
      .from('fast_track_feedback')
      .select('id')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        setFeedbackSubmitted(!!data);
        setCheckingFeedback(false);
      });
  }, [allCompleteCheck, user?.id]);

  const handleFeedbackSubmit = async () => {
    const { q1, q2, q3, q4, q5, q6, improvement_text } = feedbackForm;
    if ([q1, q2, q3, q4, q5, q6].some(v => v === 0)) return;
    setFeedbackLoading(true);
    const { error } = await supabase.from('fast_track_feedback').insert({
      user_id: user!.id,
      q1, q2, q3, q4, q5, q6,
      improvement_text: improvement_text.trim() || null,
    });
    setFeedbackLoading(false);
    if (!error) {
      setFeedbackSubmitted(true);
      // Collapse the feedback card — the persistent banner below handles Phase 3
      setExpandedWeek(null);
    }
  };

  if (!user) return null;

  const weeks = user.fastTrackWeeks;
  const currentWeekIdx = weeks.findIndex(w => !w.completed);
  const completedCount = weeks.filter(w => w.completed).length;
  const allComplete = completedCount === 5;

  // Feedback step derived state
  const isFeedbackLocked = !allComplete;
  const isFeedbackDone = feedbackSubmitted;
  const isFeedbackActive = allComplete && !feedbackSubmitted;
  const isFeedbackExpanded = !isFeedbackLocked && (
    expandedWeek === null ? isFeedbackActive : expandedWeek === FEEDBACK_STEP
  );

  // Parse display time from "Tuesdays at 8:00 PM ET (5:00 PM PT)"
  const callTime = cohortCallTime?.split(' at ')?.[1] ?? cohortCallTime ?? '';
  // Cohort's current curriculum week (1-5), converted to 0-indexed for array lookups
  const cohortWeekIdx = Math.max(0, Math.min(4, (currentWeek ?? 1) - 1));
  const nextCallDate = cohortCallTime ? format(getNextCallDate(cohortCallTime), 'EEEE, MMMM d') : '';

  // In recorded mode the hero tracks the member's OWN progress week, not the
  // cohort's date-rotated curriculum week -- there is no cohort and no calendar.
  const heroWeekIdx = isRecorded
    ? Math.max(0, currentWeekIdx)
    : cohortWeekIdx;
  const recordingFor = (weekIdx: number) => recordings[weekIdx]?.trim() || '';
  const heroRecording = recordingFor(heroWeekIdx);

  const openWeek = (weekIdx: number) => {
    setExpandedWeek(weekIdx);
    // Wait a frame so the expand animation has laid the card out before scrolling.
    requestAnimationFrame(() => {
      document.getElementById(`fast-track-week-${weekIdx}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  const handleSubmit = () => {
    if (confirmWeek === null) return;
    const updated = [...user.fastTrackWeeks];
    updated[confirmWeek] = { ...updated[confirmWeek], completed: true, homeworkSubmitted: true };
    updateUser({ fastTrackWeeks: updated });
    setModalOpen(false);
    setConfirmWeek(null);
    setExpandedWeek(null);
    setShowCelebration(true);
    setTimeout(() => setShowCelebration(false), 3000);
  };

  const toggleHomeworkItem = (weekIdx: number, itemIdx: number) => {
    const updated = [...user.fastTrackWeeks];
    const items = [...(updated[weekIdx].homeworkItems || [])];

    // Live mode: ticking item 0 ("attend the call") must first ask live vs replay.
    // Recorded mode: item 0 is "watch the recording" and is a plain toggle.
    if (itemIdx === 0 && !items[0] && !isRecorded) {
      setPendingAttendanceWeek(weekIdx);
      setAttendanceModalOpen(true);
      return;
    }

    const wasComplete = items[itemIdx];
    items[itemIdx] = !items[itemIdx];
    const weekUpdate: typeof updated[0] = { ...updated[weekIdx], homeworkItems: items };
    if (itemIdx === 0 && !items[0]) {
      weekUpdate.callAttendanceType = null;
    }
    updated[weekIdx] = weekUpdate;
    updateUser({ fastTrackWeeks: updated });

    if (isRecorded && itemIdx === 0 && !wasComplete) {
      supabase.from('member_events').insert({
        user_id: user.id,
        event_type: 'fast_track.recording_watched',
        properties: { week: weekIdx + 1 },
      });
    }
  };

  /** Vimeo reached the end of the week's recording: tick item 0 if it isn't already. */
  const handleRecordingFinish = (weekIdx: number) => {
    const items = weeks[weekIdx]?.homeworkItems ?? [];
    if (items[0]) return;
    toggleHomeworkItem(weekIdx, 0);
  };

  const handleAttendanceSelect = (type: 'live' | 'replay') => {
    if (pendingAttendanceWeek === null || !user) return;
    const updated = [...user.fastTrackWeeks];
    const items = [...(updated[pendingAttendanceWeek].homeworkItems || [])];
    items[0] = true;
    updated[pendingAttendanceWeek] = { ...updated[pendingAttendanceWeek], homeworkItems: items, callAttendanceType: type };
    updateUser({ fastTrackWeeks: updated });
    supabase.from('member_events').insert({
      user_id: user.id,
      event_type: 'fast_track.call_attended',
      properties: { week: pendingAttendanceWeek + 1, attendance_type: type },
    });
    setAttendanceModalOpen(false);
    setPendingAttendanceWeek(null);
  };

  const allItemsComplete = (weekIdx: number) => {
    const items = weeks[weekIdx]?.homeworkItems ?? [];
    const expectedCount = WEEK_TASK_COUNTS[weekIdx] ?? 0;
    return expectedCount > 0 && items.length >= expectedCount && items.slice(0, expectedCount).every(Boolean);
  };

  const feedbackAllAnswered = ![1,2,3,4,5,6].some(n => feedbackForm[`q${n}` as keyof typeof feedbackForm] === 0);

  return (
    <div>
      <PhaseIntroCard
        phase={2}
        title="Fast Track"
        icon={BookOpen}
        description={isRecorded
          ? "Fast Track is your five-session on-ramp into the First Deal program, covering the five core topics that get you fully up to speed. Watch each session, finish the homework, and the next session unlocks. Go at your own pace."
          : "Fast Track is your five-week on-ramp into the First Deal program. Over five weeks, we'll cover the five core topics that get you fully up to speed. Show up live, ask questions, do the homework, and you'll be well on your way to your first deal."}
      />

      {/* Next-up card (recorded mode) — hidden once all 5 weeks are complete */}
      {!loading && !allCompleteCheck && isRecorded && (
        <div className="mb-8 rounded-2xl overflow-hidden shadow-lg border-l-4 border-gold" style={{ background: '#1a1714' }}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5 px-4 sm:px-7 py-5 sm:py-6">
            <div className="flex items-center gap-4 sm:gap-5 flex-1 min-w-0">
              <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(255,255,255,0.08)' }}>
                <Play className="h-5 w-5 sm:h-6 sm:w-6 text-white/70" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-gold inline-block" />
                  <span className="text-[11px] font-bold text-gold uppercase tracking-widest">
                    Next Up In Fast Track
                  </span>
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-white leading-tight">
                  {WEEK_ORDINALS[heroWeekIdx]}: {WEEK_TOPICS[heroWeekIdx]}
                </h3>
                <p className="text-sm text-white/60 mt-2">
                  {heroRecording
                    ? 'Watch the session, then complete this week\'s homework to unlock the next one.'
                    : 'This recording is not available yet. Check back soon.'}
                </p>
              </div>
            </div>
            {heroRecording ? (
              <button
                onClick={() => openWeek(heroWeekIdx)}
                className="flex items-center justify-center gap-2 px-6 py-3 gradient-gold text-primary-foreground font-semibold rounded-xl shadow-gold hover:opacity-90 transition-opacity whitespace-nowrap shrink-0 text-base"
              >
                Watch Now <Play className="h-4 w-4" />
              </button>
            ) : (
              <span className="flex items-center justify-center px-6 py-3 rounded-xl text-sm font-semibold text-white/50 border border-white/15 whitespace-nowrap shrink-0">
                Recording coming soon
              </span>
            )}
          </div>
        </div>
      )}

      {/* Upcoming Call Card (live mode) — hidden once all 5 weeks are complete */}
      {!loading && !allCompleteCheck && !isRecorded && (
        cohortCallTime ? (
          <div className="mb-8 rounded-2xl overflow-hidden shadow-lg border-l-4 border-gold" style={{ background: '#1a1714' }}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5 px-4 sm:px-7 py-5 sm:py-6">
              <div className="flex items-center gap-4 sm:gap-5 flex-1 min-w-0">
                <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'rgba(255,255,255,0.08)' }}>
                  <Video className="h-5 w-5 sm:h-6 sm:w-6 text-white/70" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-gold inline-block" />
                    <span className="text-[11px] font-bold text-gold uppercase tracking-widest">
                      Upcoming Fast Track Call
                    </span>
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl font-bold text-white leading-tight">
                    {WEEK_ORDINALS[cohortWeekIdx]}: {WEEK_TOPICS[cohortWeekIdx]}
                  </h3>
                  <div className="flex flex-wrap items-center gap-1 mt-2">
                    {nextCallDate && (
                      <span className="flex items-center gap-1.5 text-sm text-white/60">
                        <Calendar className="h-3.5 w-3.5 shrink-0" />
                        {nextCallDate}
                      </span>
                    )}
                    {nextCallDate && callTime && (
                      <span className="text-white/30 mx-1">•</span>
                    )}
                    {callTime && (
                      <span className="flex items-center gap-1.5 text-sm text-white/60">
                        <Clock className="h-3.5 w-3.5 shrink-0" />
                        {callTime}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <a
                href={cohortCallLink || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-6 py-3 gradient-gold text-primary-foreground font-semibold rounded-xl shadow-gold hover:opacity-90 transition-opacity whitespace-nowrap shrink-0 text-base"
              >
                Join Call Now <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        ) : (
          <div className="mb-8 rounded-2xl border border-border bg-card p-6 text-center">
            <p className="text-sm text-muted-foreground">No upcoming calls scheduled. Check back soon.</p>
          </div>
        )
      )}

      {/* Section header */}
      <div className="flex items-center justify-between mb-6">
        <div className="min-w-0">
          <h2 className="font-serif text-xl sm:text-2xl font-bold text-foreground">Fast Track Journey</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isRecorded
              ? '5-session progression to accelerate your journey. Go at your own pace.'
              : '5-week intensive progression to accelerate your journey.'}
          </p>
        </div>
        {allComplete && feedbackSubmitted && !checkingFeedback ? (
          <button
            onClick={() => updateUser({ phase: 3 })}
            className="flex items-center gap-2 px-4 py-2 gradient-gold text-primary-foreground text-sm font-semibold rounded-lg shadow-gold hover:opacity-90 transition-opacity whitespace-nowrap shrink-0 ml-4"
          >
            Move to Phase 3 <ArrowRight className="h-3.5 w-3.5" />
          </button>
        ) : (
          <span className="text-sm text-muted-foreground bg-muted px-3 py-1.5 rounded-full font-medium shrink-0 ml-4">
            {completedCount} / 5 {isRecorded ? 'sessions' : 'weeks'} completed
          </span>
        )}
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical connector line */}
        <div className="absolute left-5 top-5 bottom-5 w-px bg-border" />

        <div className="space-y-3">

          {/* Week cards (1-5) */}
          {weeks.map((week, i) => {
            const isActive = i === currentWeekIdx;
            const isLocked = i > currentWeekIdx && currentWeekIdx !== -1;
            const isDone = week.completed;
            const weekTasks = getWeekTasks(i, mode);
            const isExpanded = !isLocked && (expandedWeek === null ? isActive : expandedWeek === i);
            const canSubmit = allItemsComplete(i);
            const completedItems = (week.homeworkItems ?? []).filter(Boolean).length;
            const totalItems = WEEK_TASK_COUNTS[i];
            const isLastWeek = i === 4;
            const weekRecording = recordingFor(i);

            return (
              <motion.div
                key={i}
                id={`fast-track-week-${i}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex gap-4 scroll-mt-20"
              >
                {/* Circle indicator */}
                <div className={`h-10 w-10 shrink-0 rounded-full border-2 flex items-center justify-center z-10 bg-background ${
                  isDone
                    ? 'border-gold'
                    : isActive
                    ? 'border-gold'
                    : 'border-border'
                }`}>
                  {isDone ? (
                    <Check className="h-4 w-4 text-gold" />
                  ) : isLocked ? (
                    <Lock className="h-4 w-4 text-muted-foreground/50" />
                  ) : (
                    <span className="text-sm font-bold text-gold">{i + 1}</span>
                  )}
                </div>

                {/* Week card */}
                <div className={`flex-1 rounded-xl border overflow-hidden transition-all ${
                  isDone
                    ? 'border-border bg-card'
                    : isActive
                    ? 'border-gold/40 bg-card shadow-sm'
                    : 'border-border bg-card opacity-50'
                }`}>
                  {/* Header row */}
                  <div
                    className={`flex items-center justify-between px-5 py-4 transition-colors ${!isLocked ? 'cursor-pointer hover:bg-muted/30' : 'cursor-default'}`}
                    onClick={!isLocked ? () => setExpandedWeek(prev => (prev === null ? isActive : prev === i) ? null : i) : undefined}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-foreground">
                          Week {i + 1}: {WEEK_TOPICS[i]}
                        </h3>
                        {isActive && !isDone && (
                          <span className="text-xs font-semibold text-gold bg-gold/10 px-2 py-0.5 rounded-full">
                            Current Week
                          </span>
                        )}
                        {isDone && (
                          <span className="text-xs font-semibold text-gold bg-gold/10 px-2 py-0.5 rounded-full">
                            Completed
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{WEEK_DESCRIPTIONS[i]}</p>
                    </div>
                    {isDone ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); setExpandedWeek(prev => (prev === i) ? null : i); }}
                        className="p-1.5 rounded-md text-muted-foreground/50 hover:text-muted-foreground hover:bg-muted/50 transition-colors shrink-0 ml-4"
                        title="View completed work"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground font-medium shrink-0 ml-4">
                        {completedItems}/{totalItems} Tasks
                      </span>
                    )}
                  </div>

                  {/* Expanded task list */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        {/* Session recording (recorded mode only) */}
                        {isRecorded && (
                          <div className="border-t border-border">
                            {weekRecording ? (
                              <VimeoEmbed
                                src={weekRecording}
                                title={`Fast Track Week ${i + 1}: ${WEEK_TOPICS[i]}`}
                                onFinish={() => handleRecordingFinish(i)}
                              />
                            ) : (
                              <div className="aspect-video w-full bg-muted flex flex-col items-center justify-center gap-2 text-center px-6">
                                <Video className="h-8 w-8 text-muted-foreground/50" />
                                <p className="text-sm font-medium text-muted-foreground">Recording coming soon</p>
                                <p className="text-xs text-muted-foreground/70 max-w-xs">
                                  This session has not been posted yet. You can still work through the rest of the homework below.
                                </p>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="border-t border-border px-5 pt-4 pb-1">
                          <p className="text-[10px] font-bold tracking-[0.15em] text-muted-foreground uppercase mb-3">
                            Week {i + 1} Assignments
                          </p>
                          {isActive && !isDone && (
                            <div className="mb-4 flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-gold/10 border border-gold/20">
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="h-5 w-5 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                                  <span className="text-[9px] font-bold text-gold">{completedItems}</span>
                                </div>
                                <p className="text-xs text-gold/90 font-medium">
                                  {completedItems === totalItems
                                    ? `All items complete! Submit below to unlock ${isLastWeek ? 'Fast Track Feedback' : isRecorded ? 'the next session' : 'the next week'}.`
                                    : `Complete all ${totalItems} items to unlock ${isLastWeek ? 'Fast Track Feedback' : `Week ${i + 2}`}.`}
                                </p>
                              </div>
                              <span className="text-xs font-bold text-gold shrink-0">{completedItems}/{totalItems}</span>
                            </div>
                          )}
                          <WeekHomework
                            tasks={weekTasks}
                            homeworkItems={week.homeworkItems}
                            onToggleItem={(itemIdx) => toggleHomeworkItem(i, itemIdx)}
                            onStartDeclaration={() => navigate('/financial-freedom-declaration')}
                            onGoToCourse={() => navigate('/content/course')}
                          />
                        </div>

                        {isActive && !isDone && (
                          <div className="px-5 pb-5 pt-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); setConfirmWeek(i); setModalOpen(true); }}
                              disabled={!canSubmit}
                              className={`w-full py-3 text-sm font-semibold rounded-lg transition-all ${
                                canSubmit
                                  ? 'gradient-gold text-primary-foreground shadow-gold hover:opacity-90'
                                  : 'bg-muted text-muted-foreground cursor-not-allowed opacity-60 border border-border'
                              }`}
                            >
                              {canSubmit
                                ? isLastWeek
                                  ? 'Submit Week 5 & Unlock Fast Track Feedback'
                                  : isRecorded
                                    ? `Submit Week ${i + 1} & Unlock Next Session`
                                    : `Submit Week ${i + 1} & Unlock Next Week`
                                : 'Complete all tasks above to submit'}
                            </button>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}

          {/* Fast Track Feedback step */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 5 * 0.05 }}
            className="flex gap-4"
          >
            {/* Circle indicator */}
            <div className={`h-10 w-10 shrink-0 rounded-full border-2 flex items-center justify-center z-10 bg-background ${
              isFeedbackDone || isFeedbackActive ? 'border-gold' : 'border-border'
            }`}>
              {isFeedbackDone ? (
                <Check className="h-4 w-4 text-gold" />
              ) : isFeedbackLocked ? (
                <Lock className="h-4 w-4 text-muted-foreground/50" />
              ) : (
                <MessageSquare className="h-4 w-4 text-gold" />
              )}
            </div>

            {/* Feedback card */}
            <div className={`flex-1 rounded-xl border overflow-hidden transition-all ${
              isFeedbackDone
                ? 'border-border bg-card'
                : isFeedbackActive
                ? 'border-gold/40 bg-card shadow-sm'
                : 'border-border bg-card opacity-50'
            }`}>
              {/* Header */}
              <div
                className={`flex items-center justify-between px-5 py-4 transition-colors ${
                  !isFeedbackLocked ? 'cursor-pointer hover:bg-muted/30' : 'cursor-default'
                }`}
                onClick={!isFeedbackLocked ? () =>
                  setExpandedWeek(prev =>
                    (prev === null ? isFeedbackActive : prev === FEEDBACK_STEP) ? null : FEEDBACK_STEP
                  )
                : undefined}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-foreground">Fast Track Feedback</h3>
                    {isFeedbackActive && (
                      <span className="text-xs font-semibold text-gold bg-gold/10 px-2 py-0.5 rounded-full">
                        Unlocks Phase 3
                      </span>
                    )}
                    {isFeedbackDone && (
                      <span className="text-xs font-semibold text-gold bg-gold/10 px-2 py-0.5 rounded-full">
                        Completed
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {isFeedbackDone
                      ? 'You\'ve completed Fast Track. Time to move to Phase 3.'
                      : 'Share your experience from the past 5 weeks to unlock Phase 3.'}
                  </p>
                </div>
              </div>

              {/* Expanded content */}
              <AnimatePresence>
                {isFeedbackExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    {checkingFeedback ? (
                      <div className="border-t border-border flex items-center justify-center py-10">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : isFeedbackDone ? (
                      /* Completed state */
                      <div className="border-t border-border px-6 py-6 text-center">
                        <PartyPopper className="h-10 w-10 text-gold mx-auto mb-3" />
                        <h3 className="font-serif text-xl font-bold text-foreground mb-2">Fast Track Complete!</h3>
                        <p className="text-sm text-muted-foreground">
                          You've finished all 5 weeks and shared your feedback. Your Phase 3 unlock is ready below.
                        </p>
                      </div>
                    ) : (
                      /* Feedback form */
                      <div className="border-t border-border p-6 space-y-6">
                        <div>
                          <p className="text-[10px] font-bold tracking-[0.15em] text-muted-foreground uppercase mb-1">Fast Track Feedback</p>
                          <p className="text-sm text-muted-foreground mb-5">
                            Rate each statement from 1 (Strongly Disagree) to 5 (Strongly Agree). Your honest feedback helps us make Fast Track better for future members.
                          </p>
                          <div className="space-y-5">
                            {FEEDBACK_QUESTIONS.map((question, idx) => {
                              const key = `q${idx + 1}` as keyof typeof feedbackForm;
                              const val = feedbackForm[key] as number;
                              return (
                                <div key={idx}>
                                  <p className="text-sm font-medium text-foreground mb-2.5">{idx + 1}. {question}</p>
                                  <div className="flex items-center gap-2">
                                    {[1, 2, 3, 4, 5].map(n => (
                                      <button
                                        key={n}
                                        onClick={() => setFeedbackForm(prev => ({ ...prev, [key]: n }))}
                                        className={`flex-1 min-h-[44px] py-2.5 rounded-lg text-sm font-bold border transition-all ${
                                          val === n
                                            ? 'bg-gold text-white border-gold shadow-sm'
                                            : 'bg-card text-muted-foreground border-border hover:border-gold/40 hover:text-gold'
                                        }`}
                                      >
                                        {n}
                                      </button>
                                    ))}
                                  </div>
                                  <div className="flex justify-between mt-1">
                                    <span className="text-[10px] text-muted-foreground">Strongly Disagree</span>
                                    <span className="text-[10px] text-muted-foreground">Strongly Agree</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-foreground mb-2 block">
                            If there was one thing you would change or improve about Fast Track (if anything), what would it be?
                            <span className="text-muted-foreground font-normal ml-1">(Optional)</span>
                          </label>
                          <textarea
                            value={feedbackForm.improvement_text}
                            onChange={e => setFeedbackForm(prev => ({ ...prev, improvement_text: e.target.value }))}
                            placeholder="Share your thoughts..."
                            rows={4}
                            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                          />
                        </div>

                        {!feedbackAllAnswered && (
                          <p className="text-xs text-muted-foreground">Please answer all 6 questions above to submit.</p>
                        )}

                        <button
                          onClick={handleFeedbackSubmit}
                          disabled={feedbackLoading || !feedbackAllAnswered}
                          className="w-full py-3 gradient-gold text-primary-foreground font-semibold rounded-lg shadow-gold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {feedbackLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                          Submit Feedback & Unlock Phase 3
                        </button>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

        </div>
      </div>

      {/* Persistent Phase 3 unlock banner — always visible once feedback is submitted */}
      {allComplete && feedbackSubmitted && !checkingFeedback && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 rounded-xl border border-gold/40 bg-gold/10 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full gradient-gold-radial flex items-center justify-center shrink-0 shadow-gold">
              <PartyPopper className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <p className="font-semibold text-foreground leading-snug">Fast Track Complete!</p>
              <p className="text-sm text-muted-foreground mt-0.5">You've finished all 5 weeks. Phase 3 is ready for you.</p>
            </div>
          </div>
          <button
            onClick={() => updateUser({ phase: 3 })}
            className="flex items-center justify-center gap-2 px-5 py-2.5 gradient-gold text-primary-foreground font-semibold rounded-lg shadow-gold hover:opacity-90 transition-opacity whitespace-nowrap sm:shrink-0"
          >
            Move to Phase 3 <ArrowRight className="h-4 w-4" />
          </button>
        </motion.div>
      )}

      {/* Celebration overlay */}
      <AnimatePresence>
        {showCelebration && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
          >
            <div className="text-center p-8 rounded-2xl bg-card/95 border border-gold/30 shadow-gold backdrop-blur-sm pointer-events-auto">
              <motion.div
                initial={{ rotate: -10, scale: 0 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 10 }}
              >
                <PartyPopper className="h-16 w-16 text-gold mx-auto mb-4" />
              </motion.div>
              <h3 className="font-serif text-2xl font-bold text-gold mb-2">Week Complete!</h3>
              <p className="text-muted-foreground">Amazing work! Keep the momentum going</p>
              <div className="flex items-center justify-center gap-2 mt-3">
                <Sparkles className="h-4 w-4 text-gold" />
                <span className="text-sm text-gold font-medium">You're crushing it!</span>
                <Sparkles className="h-4 w-4 text-gold" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Live-vs-replay prompt. Live mode only: in recorded mode there is no call to have attended. */}
      {!isRecorded && (
        <CallAttendanceModal
          open={attendanceModalOpen}
          weekNumber={pendingAttendanceWeek !== null ? pendingAttendanceWeek + 1 : 1}
          weekTitle={pendingAttendanceWeek !== null ? (WEEK_TOPICS[pendingAttendanceWeek] ?? '') : ''}
          onSelect={handleAttendanceSelect}
        />
      )}

      {/* Confirm submission modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-serif text-foreground">
              Complete Week {confirmWeek !== null && confirmWeek + 1}: {confirmWeek !== null && WEEK_TOPICS[confirmWeek]}?
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Confirm that you've finished all the homework for this week.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-2">
            <Button
              variant="outline"
              onClick={() => setModalOpen(false)}
              className="flex-1"
            >
              Not Yet
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1 gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90"
            >
              Yes, I'm Done!
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Phase2;
